import { useEffect, useState } from 'react';
import { Sparkles, Loader2, Check, AlertCircle, Wand2 } from 'lucide-react';
import adminAxios from '../lib/adminAxios';
import useProducts from '../hooks/useProducts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODELS = [
  { id: 'gemini', label: 'Gemini Nano Banana', hint: 'Best for editing/composing real reference photos' },
  { id: 'openai', label: 'OpenAI GPT Image 1', hint: 'Best for pure text-to-image, no reference photos' },
];

export default function ImageStudioPanel() {
  const { products } = useProducts();
  const allCodes = Object.keys(products).sort((a, b) => a.localeCompare(b));

  const [prompt, setPrompt] = useState('');
  const [model, setModel] = useState('gemini');
  const [refs, setRefs] = useState(new Set());
  const [saveKind, setSaveKind] = useState('standalone');
  const [saveTarget, setSaveTarget] = useState('');
  const [saveVariant, setSaveVariant] = useState('');

  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [bust, setBust] = useState(Date.now());

  const [gallery, setGallery] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(true);

  const loadGallery = async () => {
    setGalleryLoading(true);
    try {
      const { data } = await adminAxios.get(`${API}/admin/image-studio/gallery`);
      setGallery(data);
    } catch {
      setGallery([]);
    } finally {
      setGalleryLoading(false);
    }
  };

  useEffect(() => { loadGallery(); }, []);

  const toggleRef = (code) => setRefs((s) => {
    const n = new Set(s);
    n.has(code) ? n.delete(code) : n.add(code);
    return n;
  });

  const generate = async () => {
    if (!prompt.trim()) return;
    setBusy(true);
    setResult(null);
    try {
      const { data } = await adminAxios.post(`${API}/admin/image-studio/generate`, {
        prompt: prompt.trim(),
        model,
        reference_codes: model === 'gemini' ? [...refs] : null,
        save_kind: saveKind,
        save_target: saveKind === 'standalone' ? null : saveTarget.trim(),
        save_variant: saveVariant.trim() || null,
      });
      setResult({ ok: true, ...data });
      setBust(Date.now());
      if (saveKind === 'standalone') loadGallery();
    } catch (e) {
      setResult({ ok: false, msg: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="max-w-[1400px] mx-auto px-6 md:px-12 pb-24" data-testid="admin-image-studio">
      <div className="mb-10">
        <div className="eyebrow text-gold mb-4">Image Studio</div>
        <h2 className="font-display text-3xl md:text-4xl text-mb">Generate anything.</h2>
        <p className="mt-3 text-[13px] text-mb-muted max-w-xl leading-relaxed">
          Free-form prompt, either model, optional reference photos from the catalogue.
          Save straight onto a product, a set hero, or just keep it in the scratch gallery below.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-10">
        {/* Form */}
        <div className="space-y-6">
          <div>
            <label className="mb-label">Prompt</label>
            <textarea
              rows={4}
              className="mb-input"
              placeholder="e.g. A close-up of hand-stitched leather edging, near-black background, single hard light, editorial, no text"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              data-testid="studio-prompt-input"
            />
          </div>

          <div>
            <label className="mb-label">Model</label>
            <div className="mt-2 grid grid-cols-2 gap-3">
              {MODELS.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setModel(m.id)}
                  data-testid={`studio-model-${m.id}`}
                  className={`text-left p-3 hairline transition-colors ${
                    model === m.id ? 'border-gold bg-mb-surface' : 'border-subtle hover:border-mb-muted'
                  }`}
                >
                  <div className={`eyebrow-sm ${model === m.id ? 'text-gold' : 'text-mb'}`}>{m.label}</div>
                  <div className="mt-1 text-[11px] text-mb-muted leading-snug">{m.hint}</div>
                </button>
              ))}
            </div>
          </div>

          {model === 'gemini' && (
            <div>
              <div className="flex items-center justify-between">
                <label className="mb-label">Reference photos (optional)</label>
                {refs.size > 0 && (
                  <span className="eyebrow-sm text-gold">{refs.size} selected</span>
                )}
              </div>
              <div className="mt-2 hairline border-subtle p-3 max-h-[200px] overflow-y-auto">
                <div className="grid grid-cols-2 gap-1">
                  {allCodes.map((code) => {
                    const on = refs.has(code);
                    return (
                      <button
                        key={code}
                        type="button"
                        onClick={() => toggleRef(code)}
                        data-testid={`studio-ref-${code.replace(/\s/g, '-')}`}
                        className={`text-left px-2 py-1.5 hairline text-[12px] transition-colors ${
                          on ? 'border-gold text-gold bg-mb' : 'border-subtle text-mb-muted hover:text-mb'
                        }`}
                      >
                        {on ? '●' : '○'} <span className="text-gold">{code}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          <div>
            <label className="mb-label">Save as</label>
            <div className="mt-2 flex gap-3">
              {['standalone', 'product', 'set'].map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setSaveKind(k)}
                  data-testid={`studio-savekind-${k}`}
                  className={`eyebrow-sm px-3 py-1.5 hairline transition-colors ${
                    saveKind === k ? 'border-gold text-gold' : 'border-subtle text-mb-muted hover:text-mb'
                  }`}
                >
                  {k === 'standalone' ? 'Scratch gallery' : k === 'product' ? 'Product image' : 'Set hero'}
                </button>
              ))}
            </div>
            {saveKind !== 'standalone' && (
              <div className="mt-3 grid grid-cols-2 gap-3">
                <input
                  className="mb-input"
                  placeholder={saveKind === 'product' ? 'Product code, e.g. GV 4439' : 'Set slug, e.g. the-legacy'}
                  value={saveTarget}
                  onChange={(e) => setSaveTarget(e.target.value)}
                  data-testid="studio-save-target"
                />
                {saveKind === 'product' && (
                  <input
                    className="mb-input"
                    placeholder="Variant (optional, defaults to clean)"
                    value={saveVariant}
                    onChange={(e) => setSaveVariant(e.target.value)}
                    data-testid="studio-save-variant"
                  />
                )}
              </div>
            )}
          </div>

          <button
            className="btn-gold disabled:opacity-50"
            disabled={busy || !prompt.trim() || (saveKind !== 'standalone' && !saveTarget.trim())}
            onClick={generate}
            data-testid="studio-generate-btn"
          >
            {busy
              ? <><Loader2 size={14} className="animate-spin" /> Generating…</>
              : <><Wand2 size={14} strokeWidth={1.25} /> Generate</>}
          </button>

          {result && (
            <div className={`hairline p-4 ${result.ok ? 'border-gold' : 'border-subtle'}`} data-testid="studio-result">
              <div className={`flex items-center gap-2 eyebrow-sm ${result.ok ? 'text-gold' : 'text-mb-muted'}`}>
                {result.ok ? <Check size={14} /> : <AlertCircle size={14} />}
                {result.ok ? `Saved → ${result.image}` : result.msg}
              </div>
            </div>
          )}
        </div>

        {/* Preview */}
        <div>
          <label className="mb-label">Preview</label>
          <div className="mt-2 aspect-[4/5] bg-mb hairline border-subtle flex items-center justify-center overflow-hidden">
            {busy ? (
              <Loader2 size={24} className="animate-spin text-gold" />
            ) : result?.ok ? (
              <img
                key={bust}
                src={`${result.image}?t=${bust}`}
                alt="Generated"
                className="w-full h-full object-cover"
                data-testid="studio-preview-image"
              />
            ) : (
              <div className="text-center px-6">
                <Sparkles size={20} strokeWidth={1} className="text-mb-muted/40 mx-auto mb-2" />
                <div className="eyebrow-sm text-mb-muted">Nothing generated yet</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Scratch gallery */}
      <div className="mt-14">
        <div className="eyebrow text-mb-muted mb-4">Scratch gallery</div>
        {galleryLoading ? (
          <div className="flex items-center gap-2 text-mb-muted eyebrow-sm">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        ) : gallery.length === 0 ? (
          <p className="eyebrow-sm text-mb-muted">Nothing saved to the scratch gallery yet.</p>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3" data-testid="studio-gallery">
            {gallery.map((g) => (
              <a
                key={g.name}
                href={g.image}
                target="_blank"
                rel="noreferrer"
                className="aspect-square bg-mb hairline border-subtle overflow-hidden hover:border-gold/50 transition-colors"
                data-testid={`studio-gallery-item-${g.name}`}
              >
                <img src={g.image} alt={g.name} className="w-full h-full object-cover" loading="lazy" />
              </a>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
